import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Loader } from '@components/atoms/loader';
import { ToastContainer } from '@components/moleculs/toast-container';
import { useScreenFullHeight } from '@hooks/use-screen-full-height';
import { createLocalization } from '@lang/localization';
import type { VerificationProfileModel, VerificationProfileSettingsModel } from '@models';
import type { VerificationPhotosKeysEnum } from '@models/enums';
import { LangEnum } from '@models/enums';
import { DocsVerificationSteps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/docs-verification-steps';
import { VerificationError } from '@modules/dashboard/pages/docs-verification/docs-verification-page/docs-verification-steps/verification-error';
import { PersonalDataContextProvider } from '@modules/dashboard/pages/docs-verification/docs-verification-page/tabs/personal-data/provider';
import { VERIFICATION_RESPONSE } from '@modules/docs-verification-widget/demo-responses/verification-response';
import { useDeviceOrientation } from '@modules/docs-verification-widget/hooks/use-device-orientation';
import type { DomainSettingsModel } from '@modules/shared/models/white-label/domain-settings-model';
import { domainSlice } from '@modules/shared/slices/domain-slice';
import Box from '@mui/material/Box';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import type { ThemeOptions } from '@mui/material/styles';
import {
    createTheme,
    StyledEngineProvider,
    ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { themeSlice } from '@store/slices/theme-slice';
import type { StoreWrapperProps } from '@types';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import type { DefaultTheme } from 'styled-components';
import { ThemeProvider } from 'styled-components';

import { generateMediaHelpers, theme } from '../../theme';

import { useLazyGetVerificationByIdQuery } from './api';
import { GlobalStyles } from './global-styles';
import type { RootState } from './store';
import { store } from './store';
import { DocsVerificationWidgetContainer } from './styles';
import type { DocsVerificationWidgetProps } from './types';

setupListeners(store.dispatch);
createLocalization(LangEnum.EN);

const convertChecks = (settings: VerificationProfileModel['settings']) =>
    [
        ...(settings.poi_required ? settings.poi_allowed_documents : []),
        ...(settings.face_comparison_required ? settings.face_comparison_allowed_documents : []),
        ...(settings.poa_required ? ['poa'] : []),
    ] as VerificationPhotosKeysEnum[];

const StoreWrapper = ({ children }: StoreWrapperProps) => (
    <Provider store={store}>{children}</Provider>
);

export const Widget = ({
    onInit,
    settings,
    theme: customTheme,
    isDemo,
    ...rest
}: DocsVerificationWidgetProps) => {
    const externalSettings = settings as DomainSettingsModel;
    const { settings: domainSettings } = useSelector((state: RootState) => state.domain);
    const currentTheme = useSelector((state: RootState) => state.theme.currentTheme);
    const { sdkOptions } = useSelector((state: RootState) => state.docsVerification);
    const dispatch = useDispatch();
    const { isLandscape } = useDeviceOrientation();
    const mainTheme = useMemo(
        () =>
            createTheme(
                merge(cloneDeep({ ...(currentTheme as ThemeOptions) }), domainSettings.ui_settings),
                merge({ ...theme }, customTheme),
                generateMediaHelpers(currentTheme),
            ),
        [currentTheme, domainSettings, customTheme],
    ) as unknown as DefaultTheme;

    const [getVerificationById] = useLazyGetVerificationByIdQuery();
    const isInitialized = useRef(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const { height } = useScreenFullHeight();

    useEffect(() => {
        dispatch(themeSlice.actions.setIsLandscape(isLandscape));
    }, [dispatch, isLandscape]);

    useEffect(() => {
        if (externalSettings) {
            dispatch(
                domainSlice.actions.setSettings(merge(cloneDeep(domainSettings), externalSettings)),
            );
        }
        dispatch(domainSlice.actions.setInitialized(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;

            const init = async () => {
                try {
                    const verification = isDemo
                        ? VERIFICATION_RESPONSE
                        : await getVerificationById({
                              id: rest.id,
                              url: rest.apiUrl,
                          }).unwrap();

                    if (onInit) {
                        await onInit(verification.status);
                    }

                    const search = new URLSearchParams(window.location.search);
                    const source = search.get('source');

                    dispatch(
                        docsVerificationSlice.actions.useSDK({
                            id: rest.id,
                            elementId: rest.elementId,
                            settings: verification.settings as VerificationProfileSettingsModel,
                            checks: verification.checks
                                ? convertChecks(
                                      verification.settings as VerificationProfileSettingsModel,
                                  )
                                : [],
                            apiUrl: rest.apiUrl,
                            redirectUrl: rest.redirectUrl,
                            status: verification.status,
                            verificationUrl: verification.verification_url,
                            expiresAt: verification.expires_at,
                            source,
                            isDemo: !!isDemo,
                        }),
                    );
                } catch (e) {
                    if (onInit) {
                        await onInit('error', e);
                    }
                    setIsError(true);
                } finally {
                    setIsLoading(false);
                }
            };

            init();
        }
    }, [getVerificationById, rest, dispatch, onInit, isDemo]);

    const isWidgetLoading = useMemo(() => isLoading, [isLoading]);

    return (
        <StyledEngineProvider injectFirst={true}>
            <MuiThemeProvider theme={mainTheme}>
                <ThemeProvider theme={mainTheme}>
                    <PersonalDataContextProvider>
                        <DocsVerificationWidgetContainer id='dataspike-widget'>
                            <ScopedCssBaseline>
                                <GlobalStyles />
                                <ToastContainer />
                                <div>
                                    {isWidgetLoading && (
                                        <Box
                                            display='flex'
                                            justifyContent='center'
                                            alignItems='center'
                                            style={{ height }}
                                        >
                                            <Loader />
                                        </Box>
                                    )}
                                    {isError && <VerificationError />}
                                    {!isWidgetLoading && sdkOptions && (
                                        <DocsVerificationSteps onFinish={rest.onFinish} />
                                    )}
                                </div>
                            </ScopedCssBaseline>
                        </DocsVerificationWidgetContainer>
                    </PersonalDataContextProvider>
                </ThemeProvider>
            </MuiThemeProvider>
        </StyledEngineProvider>
    );
};

export const DocsVerificationWidget = (props: DocsVerificationWidgetProps) => {
    const container = document.getElementById(props.elementId);

    if (container) {
        const root = createRoot(container);

        root.render(
            <React.StrictMode>
                <StoreWrapper>
                    <Widget {...props} />
                </StoreWrapper>
            </React.StrictMode>,
        );
    }
};
